import { Alert, Button } from "antd"
import React from "react"
import { useTranslation } from "react-i18next"

import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { buildFeatureId } from "../helpers/feature-id.ts"
import { useAnalytics } from "../hooks/useAnalytics.ts"
import {
  selectFeature,
  setActiveLayers,
  setActiveTab,
  setFilters
} from "../redux/global-map.slice.ts"
import type {
  AnalyticsRecentActivityEvent,
  AnalyticsRecordsByStatusRow,
  EntityType
} from "../redux/global-map.types"
import { ActivityTrendChart } from "./ActivityTrendChart.tsx"
import { AnalyticsCard } from "./AnalyticsCard.tsx"
import { KeyInsightsRow } from "./KeyInsightsRow.tsx"
import {
  RecentActivityCard,
  RecentActivityViewAllButton
} from "./RecentActivityCard.tsx"
import {
  RecordDistributionHeatmap,
  type RecordDistributionEntityFilter,
  RecordDistributionTypeFilter
} from "./RecordDistributionHeatmap.tsx"
import { RecordsByStatusBar } from "./RecordsByStatusBar.tsx"
import { RecordsByTypeDonut } from "./RecordsByTypeDonut.tsx"
import { TopRegionsCard } from "./TopRegionsCard.tsx"
import { TopSalespeopleCard } from "./TopSalespeopleCard.tsx"

/**
 * The Analytics tab's content — mounted by `MapTab` only while
 * `activeTab === "analytics"` (mirrors `ListTab`'s own mount contract), so
 * `useAnalytics()`'s mount effect IS the "only fetch while this tab is
 * open" gate. Layout follows spec §15's own grid: KPIs live in the ALREADY
 * shared `GlobalMapHeader` above this component (trend arrows wired there,
 * not duplicated here — see that component's own doc comment for why one
 * shared KPI row beats two), three charts across the first row, Top
 * Regions/Top Salespeople/Heatmap below, Key Insights across the lower
 * section, Recent Activity lower-right.
 */
export function AnalyticsTab() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const { data, status, error, refetch } = useAnalytics()

  // Record Distribution's own type picker — held here only so the control can
  // render in its card's header row beside the title (the approved mockup's
  // own placement); the heatmap reads it exactly as it read its former local
  // state. Not a shared Global Map filter, and never sent to the backend.
  const [heatmapEntityFilter, setHeatmapEntityFilter] =
    React.useState<RecordDistributionEntityFilter>("all")

  const loading = status === "loading"
  const isError = status === "error" && !data

  // "Applies the corresponding entity filter" (spec §4) — the SAME
  // "Projects & Leads" layer toggle every other Global Map surface already
  // uses (`GlobalMapHeader`'s legend, `LayerToggleButtons`), not a new
  // filter field. Narrows to exactly the clicked entity; the toggle's own
  // "never zero layers" guard means there's always a way back (re-enable
  // the other layer from the toolbar).
  const handleSelectEntity = (entityType: EntityType) => {
    dispatch(setActiveLayers([entityType]))
  }

  const handleSelectStatus = (row: AnalyticsRecordsByStatusRow) => {
    if (!row.entity || row.value === null) return
    if (row.entity === "project") {
      dispatch(setFilters({ projectStatusIds: [row.value] }))
    } else {
      dispatch(setFilters({ leadStatusIds: [row.value] }))
    }
  }

  const handleSelectRegion = (region: string) => {
    dispatch(setFilters({ region: [region] }))
  }

  const handleSelectSalesperson = (userId: string) => {
    dispatch(setFilters({ salespersonIds: [userId] }))
  }

  const handleSelectEvent = (event: AnalyticsRecentActivityEvent) => {
    dispatch(
      selectFeature({
        id: buildFeatureId(event.entityType, event.recordId),
        entityType: event.entityType
      })
    )
    dispatch(setActiveTab("map"))
  }

  const handleViewAllActivity = () => {
    dispatch(setActiveTab("list"))
  }

  if (isError) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("globalMap.analytics.loadError")}
        description={error?.code}
        action={
          <Button size="small" onClick={refetch}>
            {t("globalMap.analytics.retry")}
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-3" data-testid="global-map-analytics-tab">
      <div className="grid grid-cols-1 gap-3 min-[1024px]:grid-cols-3">
        <AnalyticsCard
          title={t("globalMap.analytics.recordsByType.title")}
          loading={loading}
          empty={!loading && data?.recordsByType.every((row) => !row.count)}
        >
          {data && (
            <RecordsByTypeDonut
              rows={data.recordsByType}
              onSelectEntity={handleSelectEntity}
            />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title={t("globalMap.analytics.recordsByStatus.title")}
          loading={loading}
          empty={!loading && data?.recordsByStatus.length === 0}
        >
          {data && (
            <RecordsByStatusBar
              rows={data.recordsByStatus}
              onSelectStatus={handleSelectStatus}
            />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title={t("globalMap.analytics.activityTrend.title")}
          loading={loading}
        >
          {data && <ActivityTrendChart trend={data.activityTrend} />}
        </AnalyticsCard>
      </div>

      <div className="grid grid-cols-1 gap-3 min-[1024px]:grid-cols-3">
        <AnalyticsCard
          title={t("globalMap.analytics.topRegions.title")}
          loading={loading}
          empty={!loading && data?.topRegions.length === 0}
        >
          {data && (
            <TopRegionsCard
              rows={data.topRegions}
              onSelectRegion={handleSelectRegion}
            />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title={t("globalMap.analytics.topSalespeople.title")}
          loading={loading}
          empty={!loading && data?.topSalespeople.length === 0}
        >
          {data && (
            <TopSalespeopleCard
              rows={data.topSalespeople}
              onSelectSalesperson={handleSelectSalesperson}
            />
          )}
        </AnalyticsCard>

        <AnalyticsCard
          title={t("globalMap.analytics.heatmap.title")}
          loading={loading}
          headerExtra={
            <RecordDistributionTypeFilter
              value={heatmapEntityFilter}
              onChange={setHeatmapEntityFilter}
            />
          }
        >
          {data && (
            <RecordDistributionHeatmap
              heatmap={data.heatmap}
              entityFilter={heatmapEntityFilter}
            />
          )}
        </AnalyticsCard>
      </div>

      <div className="grid grid-cols-1 gap-3 min-[1024px]:grid-cols-[minmax(0,1fr)_320px]">
        <AnalyticsCard
          title={t("globalMap.analytics.insights.title")}
          loading={loading}
        >
          {data && <KeyInsightsRow insights={data.insights} />}
        </AnalyticsCard>

        <AnalyticsCard
          title={t("globalMap.analytics.recentActivity.title")}
          loading={loading}
          empty={!loading && data?.recentActivity.length === 0}
          headerExtra={
            <RecentActivityViewAllButton onViewAll={handleViewAllActivity} />
          }
        >
          {data && (
            <RecentActivityCard
              events={data.recentActivity}
              onSelectEvent={handleSelectEvent}
            />
          )}
        </AnalyticsCard>
      </div>
    </div>
  )
}

export default AnalyticsTab
